import React, { useEffect, useRef, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import AutoScroll from "embla-carousel-auto-scroll";
import classes from "./Tieups.module.scss";
import CustomImage from "../../CustomImage";
import useIsMobile from "../../../hooks/useIsMobile";
import { countries as countryList } from "../../../utils/constants";

interface TieupFlagImage {
  url: string;
}

interface TieupUniversityLogo {
  imageUrl: string;
}

interface TieupUniversity {
  id: number;
  name: string;
  country?: string | null;
  universityUrl?: string | null;
  slug?: string | null;
  universityLogo?: TieupUniversityLogo | null;
}

interface TieupCountry {
  id: number;
  name: string;
  flagImg?: TieupFlagImage;
  universities_row_1: TieupUniversity[];
  universities_row_2: TieupUniversity[];
}

interface TieupsData {
  id: number;
  title: string;
  countries: TieupCountry[];
}

interface Props {
  tieupsData?: TieupsData;
}

const MIN_MARQUEE_ITEMS = 8;
const MARQUEE_CENTER_THRESHOLD = 6;
const MARQUEE_CENTER_THRESHOLD_MOBILE = 2;

type UniversityWithLogo = TieupUniversity & { universityLogo: TieupUniversityLogo };

const hasLogo = (university: TieupUniversity): university is UniversityWithLogo => Boolean(university.universityLogo?.imageUrl);

const buildMarqueeTrack = (universities: TieupUniversity[], centerThreshold: number): UniversityWithLogo[] => {
  const withLogo = universities.filter(hasLogo);
  if (withLogo.length <= centerThreshold) return withLogo;

  const repeated: UniversityWithLogo[] = [];
  while (repeated.length < MIN_MARQUEE_ITEMS) {
    repeated.push(...withLogo);
  }

  return repeated;
};

const MARQUEE_SPEED = 1;
const MARQUEE_RESUME_DELAY = 200;

const COUNTRY_NAME_ALIASES: Record<string, string> = {
  "united states": "USA",
  "united states of america": "USA",
  uk: "UnitedKingdom",
};

const getCountrySlug = (countryName?: string): string | undefined => {
  if (!countryName) return undefined;
  const normalized = countryName.trim().toLowerCase();
  const aliased = COUNTRY_NAME_ALIASES[normalized] ?? countryName;
  return countryList.find((country) => country.value.toLowerCase() === aliased.toLowerCase())?.slug;
};

interface MarqueeRowProps {
  trackKey: number | string;
  universities: UniversityWithLogo[];
  direction: "forward" | "backward";
  isMobile: boolean;
}

const getUniversityUrl = (university: UniversityWithLogo): string | undefined => {
  const countrySlug = getCountrySlug(university.country ?? undefined);
  if (university.slug && countrySlug) return `/universities/${countrySlug}/${university.slug}`;
  return university.universityUrl ?? undefined;
};

const MarqueeRow = ({ trackKey, universities, direction, isMobile }: MarqueeRowProps) => {
  const isCentered = universities.length <= (isMobile ? MARQUEE_CENTER_THRESHOLD_MOBILE : MARQUEE_CENTER_THRESHOLD);
  const autoScroll = useRef(
    AutoScroll({ speed: MARQUEE_SPEED, direction, startDelay: MARQUEE_RESUME_DELAY, stopOnInteraction: false, stopOnMouseEnter: true }),
  );
  const [emblaRef, emblaApi] = useEmblaCarousel(
    { loop: !isCentered, dragFree: !isCentered, align: isCentered ? "center" : "start" },
    isCentered ? [] : [autoScroll.current],
  );

  useEffect(() => {
    emblaApi?.reInit();
  }, [emblaApi, trackKey]);

  if (!universities.length) return null;

  return (
    <div className={`${classes["marquee-row"]}${isCentered ? ` ${classes["marquee-row--single"]}` : ""}`} ref={emblaRef}>
      <div className={`${classes["marquee-track"]}${isCentered ? ` ${classes["marquee-track--single"]}` : ""}`}>
        {universities.map((university, index) => {
          const url = getUniversityUrl(university);

          return (
            <div key={`${university.id}-${index}`} className={classes["marquee-slide"]}>
              <button
                type="button"
                className={classes["logo-card"]}
                onClick={() => url && window.open(url, "_blank")}
                disabled={!url}
                aria-label={university.name}
              >
                {isMobile ? (
                  <CustomImage src={university.universityLogo.imageUrl} alt={university.name} width={110} height={52} layout="fixed" objectFit="contain" />
                ) : (
                  <CustomImage src={university.universityLogo.imageUrl} alt={university.name} width={240} height={115} layout="fixed" objectFit="contain" />
                )}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const Tieups = ({ tieupsData }: Props) => {
  const isMobile = useIsMobile(768);
  const countries =
    tieupsData?.countries?.filter((country) => (country?.universities_row_1?.length ?? 0) > 0 || (country?.universities_row_2?.length ?? 0) > 0) ?? [];
  const [activeCountryId, setActiveCountryId] = useState<number | undefined>(countries[0]?.id);

  const activeCountry = countries.find((country) => country.id === activeCountryId) ?? countries[0];

  const marqueeCenterThreshold = isMobile ? MARQUEE_CENTER_THRESHOLD_MOBILE : MARQUEE_CENTER_THRESHOLD;
  const row1 = buildMarqueeTrack(activeCountry?.universities_row_1 ?? [], marqueeCenterThreshold);
  const row2 = buildMarqueeTrack(activeCountry?.universities_row_2 ?? [], marqueeCenterThreshold);

  if (!tieupsData) return null;

  return (
    <section className={classes["tie-ups-container"]}>
      {tieupsData.title && <h2 className={classes.heading}>{tieupsData.title}</h2>}

      <div className={classes["pill-row"]}>
        {countries?.length > 0 &&
          countries.map((country) => (
            <button
              key={country.id}
              type="button"
              aria-pressed={activeCountryId === country?.id}
              className={`${classes.pill} ${activeCountryId === country?.id ? classes["pill-active"] : ""}`}
              onClick={() => setActiveCountryId(country?.id)}
            >
              {country?.flagImg?.url && <CustomImage src={country?.flagImg.url} alt={country?.name} width={20} height={14} />}
              <span>{country?.name}</span>
            </button>
          ))}
      </div>

      <div className={classes["marquee-wrapper"]}>
        <MarqueeRow trackKey={`${activeCountry?.id}-row1`} universities={row1} direction="forward" isMobile={isMobile} />
        <MarqueeRow trackKey={`${activeCountry?.id}-row2`} universities={row2} direction="backward" isMobile={isMobile} />
      </div>
    </section>
  );
};

export default Tieups;
